import React from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

import { STATUS_ORDER } from '../constants/paymentLinks.constants';
import {
  getStatusConfig,
  getStatusDescription,
} from '../utils/paymentLinks.utils';

interface LinkFiltersProps {
  searchInput: string;
  setSearchInput: (value: string) => void;
  filters: { search: string; status: string; type: string };
  quickFilterItems: Array<{ count: number; key: string; label: string }>;
  updateFilter: (field: string, value: any) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  onQuickFilter?: (key: string) => void;
  onRefresh?: () => void;
  quickFilterKey?: string;
  refreshing?: boolean;
}

export const LinkFilters = ({
  searchInput,
  setSearchInput,
  filters,
  quickFilterItems,
  updateFilter,
  clearFilters,
  hasActiveFilters,
  onQuickFilter,
  onRefresh,
  quickFilterKey,
  refreshing,
}: LinkFiltersProps) => {
  const activeFilterCount = [
    searchInput || filters.search,
    filters.status,
    filters.type,
    !filters.status && quickFilterKey && quickFilterKey !== 'total'
      ? quickFilterKey
      : '',
  ].filter(Boolean).length;

  return (
    <Box>
      <Box
        display="grid"
        gridTemplateColumns={{
          xs: '1fr',
          sm: 'minmax(260px, 1fr) 1fr',
          lg: 'minmax(360px, 1fr) 180px 160px minmax(0, 1fr) auto auto auto',
        }}
        gap={0.85}
        alignItems="center"
      >
        <TextField
          size="small"
          placeholder="Buscar por descrição, referência ou destino..."
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          inputProps={{ 'aria-label': 'Buscar links' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchInput('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              height: 40,
            },
            '& .MuiOutlinedInput-input': {
              fontSize: 13.5,
            },
          }}
        />

        <FormControl
          size="small"
          fullWidth
          sx={{ '& .MuiOutlinedInput-root': { height: 40 } }}
        >
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={filters.status}
            onChange={(event) => updateFilter('status', event.target.value)}
          >
            <MenuItem value="" title="Todos os status">
              Todos
            </MenuItem>
            {STATUS_ORDER.map((status) => {
              const config = getStatusConfig(status);
              return (
                <MenuItem
                  key={status}
                  value={status}
                  title={getStatusDescription(status)}
                >
                  {config.label}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <FormControl
          size="small"
          fullWidth
          sx={{ '& .MuiOutlinedInput-root': { height: 40 } }}
        >
          <InputLabel>Tipo</InputLabel>
          <Select
            label="Tipo"
            value={filters.type}
            onChange={(event) => updateFilter('type', event.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="amount">Valor</MenuItem>
            <MenuItem value="boleto">Boleto</MenuItem>
          </Select>
        </FormControl>

        <Box display={{ xs: 'none', lg: 'block' }} />

        <Tooltip title="Filtros aplicados">
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            startIcon={<FilterListIcon />}
            sx={{
              borderColor: 'divider',
              fontSize: 12,
              fontWeight: 700,
              height: 40,
              justifyContent: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            Filtros
            <Box
              component="span"
              sx={{
                alignItems: 'center',
                bgcolor: 'primary.light3',
                borderRadius: '50%',
                color: 'primary.main',
                display: 'inline-flex',
                fontSize: 11,
                height: 22,
                justifyContent: 'center',
                ml: 0.8,
                minWidth: 22,
                px: 0.45,
              }}
            >
              {activeFilterCount}
            </Box>
          </Button>
        </Tooltip>

        <Tooltip title="Limpar filtros">
          <span>
            <Button
              variant="text"
              color="primary"
              size="small"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              sx={{
                fontSize: 12,
                fontWeight: 700,
                minHeight: 36,
                whiteSpace: 'nowrap',
              }}
            >
              Limpar filtros
            </Button>
          </span>
        </Tooltip>

        <Tooltip title="Atualizar dados da tabela">
          <span>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => onRefresh?.()}
              disabled={refreshing || !onRefresh}
              sx={{
                borderColor: 'divider',
                fontSize: 12,
                fontWeight: 700,
                minHeight: 36,
                whiteSpace: 'nowrap',
              }}
            >
              Atualizar
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Box alignItems="center" display="flex" flexWrap="wrap" gap={0.65} mt={1}>
        {quickFilterItems.map((filter) => {
          const active = quickFilterKey === filter.key;
          return (
            <Chip
              key={filter.key}
              clickable
              color={active ? 'primary' : 'default'}
              label={filter.label}
              onClick={() => onQuickFilter?.(filter.key)}
              size="small"
              sx={{
                borderRadius: 1,
                fontSize: 12,
                fontWeight: active ? 700 : 500,
                height: 30,
                '& .MuiChip-label': {
                  alignItems: 'center',
                  display: 'inline-flex',
                  gap: 0.8,
                  px: 1,
                  '&::after': {
                    alignItems: 'center',
                    bgcolor: active ? 'primary.light3' : 'action.hover',
                    borderRadius: '50%',
                    color: active ? 'primary.main' : 'text.secondary',
                    content: `"${filter.count}"`,
                    display: 'inline-flex',
                    fontSize: 11,
                    fontWeight: 800,
                    height: 22,
                    justifyContent: 'center',
                    minWidth: 22,
                    px: 0.45,
                  },
                },
              }}
              variant={active ? 'filled' : 'outlined'}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default LinkFilters;
